import { useState } from 'react';
import { Chip } from '@nextui-org/react';
import { CtyButton, sharedPDFViewHandler, SourceElementType, ArticleLayout, pdfViewPayload, AgentViewPayload } from '@circuitry-ai/doc-ui';
import { RootState, useGetPdfLinkForConversationsMutation, useGetGuestPdfLinkForConversationsMutation, useGetAgentKowledgeSourceMutation } from '@circuitry-ai/doc-data';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface AgentKnowledgeSourcesProps {
    sourceIds: any[];
    messageId?: string;
    showAvailableFilesLabel?: boolean;
}

const AgentKnowledgeSources = (props: AgentKnowledgeSourcesProps) => {
    const { sourceIds = [], messageId = '', showAvailableFilesLabel = false } = props;
    const { t } = useTranslation();
    const { guestToken } = useSelector((state: RootState) => state.advisor);
    const { token: authToken } = useSelector((state: RootState) => state.auth);
    const [getPdfLink] = useGetPdfLinkForConversationsMutation();
    const [getGuestPdfLink] = useGetGuestPdfLinkForConversationsMutation();
    const [getAgentPdfLink] = useGetAgentKowledgeSourceMutation();
    const [pdfLoadingIndex, setPdfLoadingIndex] = useState<string>('');
    const [articleId, setArticleId] = useState<string | null>(null);
    const [isArticleReadOnly, setIsArticleReadOnly] = useState(false);

    const handleButtonAction = async (source: any, index: number) => {
        // Handle both pages and page_labels for compatibility
        const pageArray = source?.pages || source?.page_labels || [];

        if (source?.element_type === SourceElementType.EXPERT_ANSWER || source?.element_type === SourceElementType.KNOWLEDGE_ARTICLE) {
            const loadingState = source?.source_id + (pageArray.length > 0 ? pageArray[0] : '') + messageId + index;
            setPdfLoadingIndex(loadingState);
            setIsArticleReadOnly(true);
            setArticleId(source?.source_id);
        } else if (source?.element_type === SourceElementType.YOUTUBE || source?.element_type === SourceElementType.URL || source?.element_type === SourceElementType.API) {
            window.open(source?.element_refs?.[0]?.url, '_blank');
        } else {
            handlePDFViewer(source, index);
        }
    };

    const handlePDFViewer = (source: any, index: number) => {
        // Handle both pages and page_labels for compatibility
        const pageArray = source?.pages || source?.page_labels || [];

        // Helper function to extract file extension from file name
        const getFileExtensionFromFileName = (fileName: string): string => {
            if (!fileName) return '';
            const lastDotIndex = fileName.lastIndexOf('.');
            if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1) return '';
            return fileName.substring(lastDotIndex + 1).toLowerCase().trim();
        };

        // Get file name from multiple possible sources
        const fileName = source?.original_file_name || source?.file_name || source?.name || '';
        
        // Extract file extension from file name, with fallbacks
        const fileExtension = getFileExtensionFromFileName(fileName) || 
                              getFileExtensionFromFileName(source?.name || '') ||
                              source?.file_extension || 
                              source?.element_type || 
                              '';

        // Build payloads for both conversation/pdf endpoint and agent presigned-url endpoint
        const pdfLinkRequestPayload: pdfViewPayload = {
            advisor_id: '', // Empty for agent history view
            source_ids: [
                {
                    source_id: source?.source_id,
                    page_labels: pageArray.map(Number) || []
                }
            ]
        };

        const agentPayload: AgentViewPayload = {
            source_ids: [
                {
                    source_id: source?.source_id,
                    file_name: fileName,
                    file_extension: fileExtension
                }
            ]
        };

        // Choose API and payload depending on whether we should call agent API
        const isAgentApi = !!authToken; // agent context (agent history) uses agent presigned-url endpoint

        const apiPayLoad = isAgentApi ? { ...agentPayload } : { ...pdfLinkRequestPayload, ...(!authToken && { token: guestToken }) };

        const loadingState = source?.source_id + (pageArray.length > 0 ? pageArray[0] : '') + messageId + index;
        const fileType = source?.file_extension || source?.invoice_type || source?.element_type;

        // Select the correct API based on agent vs normal flow
        let getPdfDetails;
        if (isAgentApi) {
            getPdfDetails = getAgentPdfLink;
        } else {
            getPdfDetails = authToken ? getPdfLink : getGuestPdfLink;
        }

        sharedPDFViewHandler(
            loadingState,
            setPdfLoadingIndex,
            apiPayLoad,
            getPdfDetails,
            sourceIds,
            fileType,
            source,
            () => { }
        );
    };

    const getFilteredSources = () => {
        return sourceIds
            .slice()
            .sort((a: any, b: any) => {
                const rankA = a.ranking ?? Infinity;
                const rankB = b.ranking ?? Infinity;
                return rankA - rankB;
            })
            .flatMap((obj: any) => {
                // Check for both pages and page_labels for compatibility
                const pageArray = obj?.pages || obj?.page_labels;
                if (pageArray?.length > 0) {
                    // Keep pages together as a single source, don't split
                    return [obj];
                }
                return [obj];
            })
            .filter((source: any, index: number, self: any[]) => {
                return (
                    index ===
                    self.findIndex(
                        (s: any) => s.source_id === source.source_id
                    )
                );
            })
            .slice(0, 5);
    };

    const filteredSources = getFilteredSources();

    if (filteredSources.length === 0) {
        return null;
    }

    return (
        <>
            {showAvailableFilesLabel ? (
                <p className="text-sm font-semibold text-foreground mb-1">{t('Available files')}</p>
            ) : null}
            <div className="flex flex-col gap-2 w-full">
                {filteredSources.map((source: any, index: number) => {
                    const pageArray = source?.pages || source?.page_labels || [];
                    const isLoading = pdfLoadingIndex === source.source_id + (pageArray.length > 0 ? pageArray[0] : '') + messageId + index;
                    const displayName = source?.file_name || source?.comment || source?.original_file_name || `${t('Source')} ${index + 1}`;
                    const fileExtension = (displayName.split('.').pop() || source?.element_type || '').toUpperCase();

                    const status = String(source?.status ?? '').toLowerCase();
                    const statusChipColor = status === 'success' ? 'success' : status === 'skipped' ? 'warning' : null;

                    return (
                        <div
                            key={source.source_id + index}
                            className="flex items-start gap-3 p-3 border border-default-200 rounded-lg shadow-sm hover:bg-default-100 mb-3"
                        >
                            {/* Square icon box with file type */}
                            <div className="flex-shrink-0">
                                <div className="w-10 h-12 bg-primary rounded flex items-center justify-center">
                                    <span className="text-white text-xs font-medium">{fileExtension || 'FILE'}</span>
                                </div>
                            </div>
                            {/* File name and view source link */}
                            <div className="flex flex-col flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 min-w-0">
                                    <span className="text-sm font-medium text-foreground truncate">{displayName}</span>
                                    {statusChipColor && (
                                        <Chip size="sm" variant="flat" color={statusChipColor} className="shrink-0 rounded-full">
                                            {source?.status}
                                        </Chip>
                                    )}
                                </div>
                                <CtyButton
                                    size="sm"
                                    variant="light"
                                    onPress={() => handleButtonAction(source, index)}
                                    isLoading={isLoading}
                                    className="text-default-500 hover:bg-default-100 p-0 h-auto text-sm underline self-start"
                                >
                                    {t('View source')}
                                </CtyButton>
                            </div>
                        </div>
                    );
                })}
            </div>

            {articleId && (
                <ArticleLayout
                    articleId={articleId}
                    messageId={messageId}
                    sessionId=""
                    advisorId=""
                    readOnly={isArticleReadOnly}
                    conversationView
                    onClose={() => {
                        setArticleId(null);
                        setIsArticleReadOnly(false);
                        setPdfLoadingIndex('');
                    }}
                />
            )}
        </>
    );
};

export default AgentKnowledgeSources;

